import { debounce, isEqual } from 'lodash';
import { createContext, useCallback, useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  type EjnFilter,
  initialEjnFilterState,
  resetFilters,
  upsertFilters,
} from '../../store/reducers/ejn-filters';

type IEjnFiltersContext = EjnFilter & {
  changeFilter: (newFilter: Partial<EjnFilter>) => void;
  reset: () => void;
};

const defaultEjnFiltersContext: IEjnFiltersContext = {
  changeFilter: () => undefined,
  reset: () => undefined,
  ...initialEjnFilterState,
};

const EjnFiltersContext = createContext(defaultEjnFiltersContext);

export const EjnFiltersProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();

  const filter = useSelector(
    (state: RootState) => state.extrajudicialNotificationsFiltersStore,
    shallowEqual,
  );

  const changeFilter = useCallback(
    debounce((newFilter: Partial<EjnFilter>) => {
      if (!isEqual({ ...filter, ...newFilter }, filter)) {
        dispatch(upsertFilters(newFilter));
      }
    }, 300),
    [],
  );

  const reset = useCallback(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  return (
    <EjnFiltersContext.Provider
      value={{
        changeFilter,
        reset,
        ...filter,
      }}
    >
      {children}
    </EjnFiltersContext.Provider>
  );
};

export const useEjnFilters = () => {
  return useContext(EjnFiltersContext);
};
